import settings from '@/settings/public'

// Detect if we're in Node.js (backend) or browser (frontend)
const isNode = typeof process !== 'undefined' && process.versions?.node != null
// Was inverted: `|| NEXT_PUBLIC_APP_ENV === 'production'` made the "dev only"
// predicate TRUE in production, so debug.* (incl. PII-bearing analytics dumps)
// logged whenever the public NEXT_PUBLIC_DEBUG flag was on. Now genuinely
// dev-only.
const isDev = process.env.NODE_ENV !== 'production' && process.env.NEXT_PUBLIC_APP_ENV !== 'production'
const shouldLog = () => settings.debug && isDev

// ANSI codes
const ansiBold = '\x1b[1m'
const ansiReset = '\x1b[0m'

// ANSI color codes for Node.js
const ansiColors = {
  blue: '\x1b[34m',
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  cyan: '\x1b[36m',
  magenta: '\x1b[35m',
  gray: '\x1b[90m',
  orange: '\x1b[38;5;208m', // 256-color mode for orange
  purple: '\x1b[38;5;129m', // 256-color mode for purple
  pink: '\x1b[38;5;213m', // 256-color mode for pink
  lime: '\x1b[92m', // bright green
  teal: '\x1b[38;5;30m', // 256-color mode for teal
  indigo: '\x1b[38;5;54m', // 256-color mode for indigo
  white: '\x1b[37m',
  black: '\x1b[30m',
  reset: ansiReset,
}

// CSS styles for browser console
const cssColors = {
  blue: 'color: #2196F3; font-weight: bold',
  red: 'color: #F44336; font-weight: bold',
  green: 'color: #4CAF50; font-weight: bold',
  yellow: 'color: #FFC107; font-weight: bold',
  cyan: 'color: #00BCD4; font-weight: bold',
  magenta: 'color: #E91E63; font-weight: bold',
  gray: 'color: #9E9E9E; font-weight: bold',
  orange: 'color: #FF9800; font-weight: bold',
  purple: 'color: #9C27B0; font-weight: bold',
  pink: 'color: #E91E9C; font-weight: bold',
  lime: 'color: #CDDC39; font-weight: bold',
  teal: 'color: #009688; font-weight: bold',
  indigo: 'color: #3F51B5; font-weight: bold',
  white: 'color: #FFFFFF; font-weight: bold',
  black: 'color: #000000; font-weight: bold',
}

// Type for color logger - callable function with methods
type ColorLogger = ((text: string, ...rest: any[]) => void) & {
  log: (text: string, ...rest: any[]) => void
  warn: (text: string, ...rest: any[]) => void
  error: (text: string, ...rest: any[]) => void
  info: (text: string, ...rest: any[]) => void
}

/**
 * Creates a color logger that can be called directly (defaults to log)
 * or used with specific methods (log, warn, error, info)
 */
const createColorLogger = (colorName: keyof typeof cssColors): ColorLogger => {
  const ansiColor = ansiColors[colorName]
  const cssColor = cssColors[colorName]

  const logMethod = (text: string, ...rest: any[]) => {
    if (shouldLog()) {
      if (isNode) {
        console.log(`${ansiBold}${ansiColor}${text}${ansiReset}`, ...rest)
      } else {
        console.log(`%c${text}`, cssColor, ...rest)
      }
    }
  }

  const warnMethod = (text: string, ...rest: any[]) => {
    if (shouldLog()) {
      if (isNode) {
        console.warn(`${ansiBold}${ansiColor}${text}${ansiReset}`, ...rest)
      } else {
        console.warn(`%c${text}`, cssColor, ...rest)
      }
    }
  }

  const errorMethod = (text: string, ...rest: any[]) => {
    // Always log errors, even in production
    if (isNode) {
      console.error(`${ansiBold}${ansiColor}${text}${ansiReset}`, ...rest)
    } else {
      console.error(`%c${text}`, cssColor, ...rest)
    }
  }

  const infoMethod = (text: string, ...rest: any[]) => {
    if (shouldLog()) {
      if (isNode) {
        console.info(`${ansiBold}${ansiColor}${text}${ansiReset}`, ...rest)
      } else {
        console.info(`%c${text}`, cssColor, ...rest)
      }
    }
  }

  // Create callable function that defaults to log
  const colorLogger = ((text: string, ...rest: any[]) => {
    logMethod(text, ...rest)
  }) as ColorLogger

  // Attach methods
  colorLogger.log = logMethod
  colorLogger.warn = warnMethod
  colorLogger.error = errorMethod
  colorLogger.info = infoMethod

  return colorLogger
}

/**
 * Unified debug logger with color support
 * Works in both frontend (browser) and backend (Node.js)
 * Only logs when settings.debug is true and in dev environment
 * Errors are always logged, even in production
 *
 * @example
 * ```ts
 * // Basic logging
 * debug('Normal message')
 * debug.log('Log message')
 * debug.warn('Warning message')
 * debug.error('Error message') // Always logged
 * debug.info('Info message')
 *
 * // Colored logging - direct call (defaults to log)
 * debug.blue('Info message')
 * debug.red('Error message', errorObject)
 * debug.green('Success message')
 *
 * // Colored logging - chained with log methods
 * debug.blue.log('Info message')
 * debug.red.warn('Warning message')
 * debug.green.error('Error message') // Always logged
 * debug.yellow.info('Info message')
 * debug.cyan.log('Debug message')
 * debug.magenta.warn('Special warning')
 * debug.gray.info('Muted info')
 * debug.orange.warn('Orange warning')
 * debug.purple.log('Purple message')
 * debug.pink.info('Pink info')
 * debug.lime.log('Lime message')
 * debug.teal.info('Teal info')
 * debug.indigo.log('Indigo message')
 * debug.white.log('White message')
 * debug.black.log('Black message')
 * ```
 */
const debugFn = (...content: any[]) => {
  if (shouldLog()) {
    console.log(...content)
  }
}

// Add standard logging methods
debugFn.log = (...args: any[]) => {
  if (shouldLog()) {
    console.log(...args)
  }
}

debugFn.warn = (...args: any[]) => {
  if (shouldLog()) {
    console.warn(...args)
  }
}

debugFn.error = (...args: any[]) => {
  // Always log errors, even in production
  console.error(...args)
}

debugFn.info = (...args: any[]) => {
  if (shouldLog()) {
    console.info(...args)
  }
}

// Add color loggers
debugFn.blue = createColorLogger('blue')
debugFn.red = createColorLogger('red')
debugFn.green = createColorLogger('green')
debugFn.yellow = createColorLogger('yellow')
debugFn.cyan = createColorLogger('cyan')
debugFn.magenta = createColorLogger('magenta')
debugFn.gray = createColorLogger('gray')
debugFn.orange = createColorLogger('orange')
debugFn.purple = createColorLogger('purple')
debugFn.pink = createColorLogger('pink')
debugFn.lime = createColorLogger('lime')
debugFn.teal = createColorLogger('teal')
debugFn.indigo = createColorLogger('indigo')
debugFn.white = createColorLogger('white')
debugFn.black = createColorLogger('black')

export const debug = debugFn
